// helpers/getTransactionIcon.ts
import { isValid, parseISO } from "date-fns";
import { currencyFormatter } from "@/lib/utils";
import { ArrowUpIcon } from "@/components/ui/icons";
import { centsToDollars } from "@/lib/number-conversion/number-conversion.utils";

export function getTransactionIconContainerClassName(type: string): string {
	switch (type) {
		case "Deposit":
			return "bg-primary-50 ring-1 ring-inset ring-primary-100";
		case "Withdrawal":
			return "bg-amber-50 ring-1 ring-inset ring-amber-200/70";
		default:
			return "bg-primary-50 ring-1 ring-inset ring-primary-100";
	}
}

export function getTransactionIcon(type: string) {
	switch (type) {
		case "Deposit":
			return <ArrowUpIcon className="fill-primary-600" />;
		case "Withdrawal":
			return <ArrowUpIcon className="rotate-180 fill-amber-700" />;
		default:
			return <ArrowUpIcon className="rotate-90 fill-primary-500" />;
	}
}

function getTransactionPrefix(type: string): string {
	switch (type) {
		case "Deposit":
			return "+";
		case "Withdrawal":
			return "-";
		default:
			return "";
	}
}

export function getTransactionStatusLabel(
	type: string,
	status: string,
): string {
	switch (status) {
		case "PENDING_REVIEW":
			return "Pending";
		case "APPROVED":
		case "CAPTURED":
			if (type === "Deposit") return "Deposited";
			if (type === "Withdrawal") return "Sent Out";
			return "Approved";
		case "REFUNDED":
			return "Refunded";
		case "PENDING":
			return "Pending";
		case "PARTIALLY_REFUNDED":
			return "Refunded";
		case "VOIDED":
		case "REVERSED":
		case "CANCELED":
			return "Voided";
		case "DECLINED":
		case "REJECTED":
			return "Failed";
		default:
			return "";
	}
}

export function getTransactionStatusClassName(status: string): string {
	switch (status) {
		case "PENDING_REVIEW":
			return "text-sm font-medium text-right text-amber-600";
		case "APPROVED":
		case "CAPTURED":
			return "text-sm font-medium text-right text-textLight";
		case "REFUNDED":
		case "PARTIALLY_REFUNDED":
			return "text-sm font-medium text-right text-blue-600";
		case "VOIDED":
		case "REVERSED":
		case "CANCELED":
		case "DECLINED":
		case "REJECTED":
			return "text-sm font-medium text-right text-utility-red";
		case "PENDING":
			return "text-sm font-medium text-right text-amber-600";
		default:
			return "text-sm font-medium text-right text-textLight";
	}
}

export function formatTransactionAmount(type: string, amount: number): string {
	const prefix = getTransactionPrefix(type);
	return `${prefix}${currencyFormatter.format(centsToDollars(amount))}`;
}

const PACIFIC_TIME_ZONE = "America/Los_Angeles";

function parseTransactionDateTime(date: string): Date | null {
	const trimmed = date.trim();
	if (!trimmed) return null;

	// API sends UTC wall time without a timezone suffix (e.g. "2026-06-22 17:34:16").
	const parsed = parseISO(`${trimmed.replace(" ", "T")}Z`);
	return isValid(parsed) ? parsed : null;
}

export function getFormattedTransactionDate(date: string) {
	const parsed = parseTransactionDateTime(date);
	if (!parsed) return "";

	return new Intl.DateTimeFormat("en-US", {
		timeZone: PACIFIC_TIME_ZONE,
		month: "long",
		day: "numeric",
		year: "numeric",
	}).format(parsed);
}

export function getFormattedTransactionTime(date: string) {
	const parsed = parseTransactionDateTime(date);
	if (!parsed) return "";

	return new Intl.DateTimeFormat("en-US", {
		timeZone: PACIFIC_TIME_ZONE,
		hour: "numeric",
		minute: "2-digit",
		timeZoneName: "short",
	}).format(parsed);
}

export function getTimePeriod(date?: Date) {
	if (!date) return undefined;
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	return `${y}-${m}`;
}
